import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Briefcase, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];


const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);


  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    cover_letter: "",
    internship_details: "",
    college: "",
  });

 const fetchJob = useCallback(async () => {
  try {
    const res = await api.get(`/careers/${id}`);
    setJob(res.data);
  } catch {
    toast.error("Failed to load job details");
  } finally {
    setLoading(false);
  }
}, [id]);

useEffect(() => {
  fetchJob();
}, [fetchJob]);

if (loading) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          
          {/* Spinner */}
          <div className="w-14 h-14 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />

          {/* Brand Text */}
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-gradient">
              CodeSky
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Loading Job Detais...
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}


  if (!job) return null;

  // 🔥 IMPORTANT CHECK
  const isInternship = job.is_internship;

  // 📤 Upload resume to Cloudinary
  const uploadResume = async () => {
    if (!resumeFile) {
      toast.error("Please upload resume");
      throw new Error("Resume missing");
    }

    const data = new FormData();
    data.append("file", resumeFile);

    const res = await api.post("/upload/resume", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data.resume_url;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    const resume_url = await uploadResume();

    await api.post("/applications", {
      career_id: job.id,
      application_type: isInternship ? "internship" : "job",
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      resume_url,
      cover_letter: isInternship ? null : formData.cover_letter,
      internship_details: isInternship ? formData.internship_details : null,
    });

    toast.success("Application submitted successfully!");
    setShowApplicationModal(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      cover_letter: "",
      internship_details: "",
      college: "",
    });
    setResumeFile(null);
  } catch {
    toast.error("Failed to submit application");
  } finally {
    setSubmitting(false);
  }
};


  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20">
  <div className="max-w-4xl mx-auto px-6">

    {/* 🔙 Back Button – FIXED */}
    <div className="mb-4">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back</span>
      </button>
    </div>

    <h1 className="text-4xl font-heading font-bold mb-4">
      {job.title}
    </h1>


          <div className="flex flex-wrap gap-6 text-slate-600 mb-8">
            <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" />{job.department}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" />{job.location}</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{job.job_type}</span>
          </div>

         {/* Description */}
<div className="prose max-w-none mb-10 text-slate-700">
  {job.description}
</div>

{/* Requirements */}
{job.requirements?.length > 0 && (
  <div className="mb-10">
    <h2 className="text-2xl font-semibold mb-4">Requirements</h2>
    <ul className="list-disc pl-6 space-y-2 text-slate-700">
      {job.requirements.map((req, index) => (
        <li key={index}>{req}</li>
      ))}
    </ul>
  </div>
)}

{/* Responsibilities */}
{job.responsibilities?.length > 0 && (
  <div className="mb-10">
    <h2 className="text-2xl font-semibold mb-4">Responsibilities</h2>
    <ul className="list-disc pl-6 space-y-2 text-slate-700">
      {job.responsibilities.map((res, index) => (
        <li key={index}>{res}</li>
      ))}
    </ul>
  </div>
)}


          <Button onClick={() => setShowApplicationModal(true)} className="bg-primary">
            Apply Now
          </Button>
        </div>
      </section>

      {/* APPLY MODAL */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Apply for {job.title} ({isInternship ? "Internship" : "Job"})
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input type="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label>Phone *</Label>
              <Input required value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

              <div>
                  <Label>College / Degree *</Label>
                  <Input required
                    value={formData.college}
                    onChange={(e) =>
                      setFormData({ ...formData, college: e.target.value })
                    }
                  />
                </div>

           {isInternship && (
  <div>
    <Label>Why do you want this internship? *</Label>
    <Textarea
      rows={5}
      required
      value={formData.internship_details}
      onChange={(e) =>
        setFormData({ ...formData, internship_details: e.target.value })
      }
    />
  </div>
)}

{!isInternship && (
  <div>
    <Label>Cover Letter *</Label>
    <Textarea
      rows={5}
      required
      value={formData.cover_letter}
      onChange={(e) =>
        setFormData({ ...formData, cover_letter: e.target.value })
      }
    />
  </div>
)}

<div>
  <Label>Upload Resume *</Label>
  <Input
    type="file"
    accept=".pdf,.doc,.docx"
    required
    onChange={(e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error("Only PDF, DOC, or DOCX files are allowed");
        e.target.value = "";
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error("Resume must be under 2MB");
        e.target.value = "";
        return;
      }

      setResumeFile(file);
    }}
  />
</div>
            <Button
  type="submit"
  className="w-full bg-primary"
  disabled={submitting}
>
  {submitting ? "Submitting..." : "Submit Application"}
</Button>

          </form>
        {submitting && (
          <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      
      {/* Spinner */}
      <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />

      {/* Brand */}
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold text-gradient">
          CodeSky
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Submitting your application...
        </p>
      </div>
    </div>
  </div>
)}
</DialogContent>

      </Dialog>

      <Footer />
    </div>
  );
};

export default JobDetails;
